import { OrganizationEntity } from '@data';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  Chapter,
  CreateChapterValues,
  IError,
  ListResponse,
  OrganizationSlackAuthorizeValues,
  UpdateChapterValues,
} from '../utils/types';
import * as api from '../api';
import * as queryKeys from '../constants/keys';

export const useOrganization = (organizationId: string | undefined): UseQueryResult<OrganizationEntity, IError> => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [queryKeys.ORGANIZATION, organizationId],
    queryFn: () => api.fetchOrganization(organizationId!),
    enabled: !!organizationId,
    staleTime: 1000,
    placeholderData: () => {
      const list = queryClient.getQueryData<ListResponse<OrganizationEntity>>([queryKeys.ORGANIZATIONS]);
      return list?.items.find(org => org.id === organizationId);
    },
  });
};

export const useOrganizationChapters = (
  organizationId: string | undefined,
  options?: { includeDisabled?: boolean },
): UseQueryResult<{ items: Chapter[]; count: number }, IError> =>
  useQuery({
    queryKey: [queryKeys.ORGANIZATION, organizationId, 'chapters', options?.includeDisabled ? 'all' : 'active'],
    queryFn: () => api.fetchOrganizationChapters(organizationId!, options),
    enabled: !!organizationId,
  });

// After any chapter mutation, also invalidate the ROLES picker cache — useListRoles backs the
// report modal's picker via /involvements/roles, which is server-resolved from the same chapter
// table. Without this, the picker keeps showing the pre-mutation list until a hard reload.
const invalidateChapterCaches = (queryClient: ReturnType<typeof useQueryClient>, organizationId: string) => {
  queryClient.invalidateQueries({ queryKey: [queryKeys.ORGANIZATION, organizationId, 'chapters'] });
  queryClient.invalidateQueries({ queryKey: [queryKeys.ROLES] });
};

export const useCreateChapter = (
  organizationId: string,
): UseMutationResult<Chapter, IError, CreateChapterValues, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: values => api.createChapter(organizationId, values),
    onSuccess: () => invalidateChapterCaches(queryClient, organizationId),
  });
};

export const useUpdateChapter = (
  organizationId: string,
): UseMutationResult<Chapter, IError, { chapterId: string; values: UpdateChapterValues }, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chapterId, values }) => api.updateChapter(organizationId, chapterId, values),
    onSuccess: () => invalidateChapterCaches(queryClient, organizationId),
  });
};

/** Redrawn before the answer comes back, so a run of clicks moves the row at once instead of a beat late. */
export const useReorderChapters = (organizationId: string): UseMutationResult<unknown, IError, string[], unknown> => {
  const queryClient = useQueryClient();
  const chaptersKey = [queryKeys.ORGANIZATION, organizationId, 'chapters'];
  return useMutation({
    mutationFn: chapterIds => api.reorderChapters(organizationId, chapterIds),
    onMutate: async chapterIds => {
      await queryClient.cancelQueries({ queryKey: chaptersKey });
      const rank = new Map(chapterIds.map((id, index) => [id, index]));
      queryClient.setQueriesData<{ items: Chapter[]; count: number }>({ queryKey: chaptersKey }, current =>
        current
          ? { ...current, items: [...current.items].sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0)) }
          : current,
      );
    },
    onSettled: () => invalidateChapterCaches(queryClient, organizationId),
  });
};

export const useDeleteChapter = (organizationId: string): UseMutationResult<void, IError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: chapterId => api.deleteChapter(organizationId, chapterId),
    onSuccess: () => invalidateChapterCaches(queryClient, organizationId),
  });
};

export const useOrganizationSlackAuthorize = (
  organizationId: string,
  values: OrganizationSlackAuthorizeValues,
): UseQueryResult<OrganizationEntity, IError> => {
  const { code } = values;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (code) {
      navigate({ search: '' }, { replace: true });
    }
  }, [code, navigate]);

  return useQuery({
    queryKey: [queryKeys.ORGANIZATION_SLACK_AUTHORIZE, organizationId],
    queryFn: async () => {
      const org = await api.authorizeSlack(organizationId, values);
      queryClient.setQueryData([queryKeys.ORGANIZATION, organizationId], org);
      return org;
    },
    enabled: !!code,
    staleTime: 1000,
  });
};

export const useUpdateOrganization = (
  organizationId: string,
): UseMutationResult<OrganizationEntity, IError, Partial<OrganizationEntity>, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: values => api.updateOrganization(organizationId, values),
    onMutate: async (updatedOrganization: Partial<OrganizationEntity>) => {
      const previousOrg = queryClient.getQueryData([queryKeys.ORGANIZATION, organizationId]) as OrganizationEntity;
      const previousOrgs = queryClient.getQueryData([queryKeys.ORGANIZATIONS]) as ListResponse<OrganizationEntity>;

      queryClient.setQueryData([queryKeys.ORGANIZATION, organizationId], (old: OrganizationEntity) => ({
        ...old,
        ...updatedOrganization,
      }));

      queryClient.setQueryData([queryKeys.ORGANIZATIONS], (old: ListResponse<OrganizationEntity>) => ({
        ...old,
        items: old.items.map(x => (x.id === previousOrg.id ? { ...x, ...updatedOrganization } : x)),
      }));

      return { previousOrg, previousOrgs };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData([queryKeys.ORGANIZATION, organizationId], context?.previousOrg);
      queryClient.setQueryData([queryKeys.ORGANIZATIONS], context?.previousOrgs);
    },
  });
};

export const useRevokeSlack = (
  organizationId: string,
): UseMutationResult<OrganizationEntity, IError, void, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.revokeSlack(organizationId),
    onMutate: async () => {
      const previousOrg = queryClient.getQueryData([queryKeys.ORGANIZATION, organizationId]) as OrganizationEntity;

      queryClient.setQueryData([queryKeys.ORGANIZATION, organizationId], (old: OrganizationEntity) => ({
        ...old,
        slackTeamInfo: null,
      }));

      return { previousOrg };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData([queryKeys.ORGANIZATION, organizationId], context?.previousOrg);
    },
  });
};

/** Ask Phill for today's report form now, so the Slack side can be walked without waiting for 17:00. */
export const useSendReportNudgeToSelf = (organizationId: string): UseMutationResult<void, IError, void, unknown> =>
  useMutation({ mutationFn: () => api.sendReportNudgeToSelf(organizationId) });
